import React, { useRef, ChangeEvent } from 'react';
import { Upload, Button, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import JSZip from 'jszip';
import { EXCLUDED_SENSITIVE_FILES } from '../../supabase/functions/_shared/excludedFolders.ts';

const { Text } = Typography;

interface FileUploaderProps {
  onFileSelect: (file: File, truncatedFiles: string[], cleanedSize: number, originalSize: number, additionallyTruncatedFiles: string[]) => void;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  loading?: boolean;
}

// Ported exclusion logic from backend
const EXCLUDED_FOLDERS = [
  'node_modules/', '.npm/', '.yarn/', 'bower_components/', '__pycache__/', '.venv/', 'venv/', 'env/', '.conda/', 'site-packages/', 'target/', '.m2/', 'build/', 'bin/', 'obj/', 'packages/', 'vendor/', 'cargo/', '.bundle/', 'vendor/bundle/', 'dist/', 'out/', '.next/', '.nuxt/', 'public/build/', '.git/', '.vscode/', '.idea/', '.vs/', '.DS_Store/', 'Thumbs.db/', 'coverage/', '.nyc_output/', 'tmp/', 'temp/', '.cache/', 'logs/', '*.log',
];
const EXCLUDED_FILE_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp', '.mp4', '.avi', '.mov', '.wmv', '.flv', '.mp3', '.wav', '.flac', '.aac', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.tar', '.gz', '.7z', '.rar', '.ttf', '.otf', '.woff', '.woff2', '.eot', '.db', '.sqlite', '.sqlite3', '.dump',
];
const MAX_ZIP_SIZE_BYTES = 50_000_000; // 50MB (not MiB)

// Helper: files less relevant for scanning
const IRRELEVANT_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.webp', '.mp4', '.avi', '.mov', '.wmv', '.flv', '.mp3', '.wav', '.flac', '.aac', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.tar', '.gz', '.7z', '.rar', '.ttf', '.otf', '.woff', '.woff2', '.eot', '.db', '.sqlite', '.sqlite3', '.dump', '.log', '.bin', '.exe', '.dll', '.so', '.dylib', '.a', '.o', '.class', '.jar', '.pyc', '.pyo', '.pyd', '.apk', '.ipa', '.app', '.dmg', '.iso', '.msi', '.cab', '.deb', '.rpm', '.img', '.tmp', '.cache', '.bak', '.swp', '.swo', '.out', '.obj', '.map', '.lock', '.psd', '.ai', '.sketch', '.xcf', '.blend', '.fbx', '.3ds', '.dae', '.stl', '.glb', '.gltf', '.csv', '.tsv', '.jsonl', '.ndjson', '.parquet', '.arrow', '.h5', '.hdf5', '.npz', '.npy', '.mat', '.rdata', '.rds', '.sav', '.sas7bdat', '.dta', '.arff', '.feather', '.orc', '.avro', '.pb', '.onnx', '.tflite', '.ckpt', '.pth', '.pt', '.weights', '.h5', '.pb', '.mlmodel', '.model', '.joblib', '.pkl', '.pickle', '.tar.gz', '.tar.bz2', '.tar.xz', '.tgz', '.tbz2', '.txz', '.lzma', '.xz', '.lz', '.z', '.bz2', '.gz', '.zip', '.rar', '.7z', '.cab', '.iso', '.img', '.dmg', '.vhd', '.vmdk', '.qcow2', '.ova', '.ovf', '.vdi', '.vhdx', '.vbox', '.vmx', '.nvram', '.vmsd', '.vmsn', '.vmss', '.vmem', '.vmtm', '.vmtx', '.vmxf'];

function shouldExcludeFile(filePath: string): boolean {
  const normalizedPath = filePath.toLowerCase();
  for (const folder of EXCLUDED_FOLDERS) {
    if (normalizedPath.includes(folder.toLowerCase())) return true;
  }
  for (const ext of EXCLUDED_FILE_EXTENSIONS) {
    if (normalizedPath.endsWith(ext.toLowerCase())) return true;
  }
  return false;
}

function isIrrelevantFile(path: string): boolean {
  const lower = path.toLowerCase();
  return IRRELEVANT_EXTENSIONS.some(ext => lower.endsWith(ext));
}

async function cleanAndTruncateZip(file: File): Promise<{ cleanedZip: File, truncatedFiles: string[], cleanedSize: number, originalSize: number, additionallyTruncatedFiles: string[] }> {
  const zip = await JSZip.loadAsync(file);
  const newZip = new JSZip();
  let totalSize = 0;
  let filesToAdd: { path: string, content: Uint8Array, size: number }[] = [];
  const truncatedFiles: string[] = [];
  const additionallyTruncatedFiles: string[] = [];

  // Collect files, filter, and measure size
  await Promise.all(Object.keys(zip.files).map(async (filename) => {
    const entry = zip.files[filename];
    if (entry.dir) return;
    if (shouldExcludeFile(filename)) return;
    const content = await entry.async('uint8array');
    filesToAdd.push({ path: filename, content, size: content.length });
    totalSize += content.length;
  }));

  console.log('[FileUploader] Original ZIP size:', file.size, 'bytes');
  console.log('[FileUploader] Size after exclusions, before truncation:', totalSize, 'bytes');

  // Truncate if needed (first pass)
  if (totalSize > MAX_ZIP_SIZE_BYTES) {
    filesToAdd.sort((a, b) => b.size - a.size);
    while (totalSize > MAX_ZIP_SIZE_BYTES && filesToAdd.length > 0) {
      const removed = filesToAdd.shift();
      if (removed) {
        truncatedFiles.push(removed.path);
        totalSize -= removed.size;
      }
    }
    console.log('[FileUploader] Truncation applied. Remaining size:', totalSize, 'bytes');
  }

  // Add remaining files to new ZIP
  for (const f of filesToAdd) {
    newZip.file(f.path, f.content);
  }
  console.log('[FileUploader] Re-zipping with', filesToAdd.length, 'files...');
  const cleanedZipBlob = await newZip.generateAsync({ 
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 } // Moderate compression for balance
  });
  
  console.log('[FileUploader] Re-zipped blob size:', cleanedZipBlob.size, 'bytes');
  console.log('[FileUploader] Target was:', totalSize, 'bytes');
  console.log('[FileUploader] Size difference:', cleanedZipBlob.size - totalSize, 'bytes');
  
  // Additional safety check: if the final blob is still too large, throw error
  if (cleanedZipBlob.size > MAX_ZIP_SIZE_BYTES) {
    console.error('[FileUploader] Final blob size exceeds limit:', cleanedZipBlob.size, 'vs', MAX_ZIP_SIZE_BYTES);
    console.error('[FileUploader] This indicates compression overhead or calculation error');
    throw new Error(`Final ZIP file size (${(cleanedZipBlob.size / 1000000).toFixed(1)}MB) exceeds 50MB limit even after aggressive truncation. Try removing more files manually.`);
  }
  
  const cleanedZipFile = new File([cleanedZipBlob], file.name.replace(/\.zip$/, '_cleaned.zip'), { type: 'application/zip' });
  console.log('[FileUploader] Created File object with size:', cleanedZipFile.size, 'bytes');

  // Smarter fallback: If still too large, remove irrelevant files in bulk
  if (cleanedZipFile.size > MAX_ZIP_SIZE_BYTES) {
    let fallbackFiles = [...filesToAdd];
    let fallbackTruncated: string[] = [];
    let excess = cleanedZipFile.size - MAX_ZIP_SIZE_BYTES;
    // Sort irrelevant files by size descending
    const irrelevant = fallbackFiles.filter(f => isIrrelevantFile(f.path)).sort((a, b) => b.size - a.size);
    let removedBytes = 0;
    for (const file of irrelevant) {
      if (removedBytes >= excess) break;
      fallbackFiles = fallbackFiles.filter(f => f.path !== file.path);
      fallbackTruncated.push(file.path);
      removedBytes += file.size;
    }
    if (fallbackTruncated.length > 0) {
      // Re-zip after bulk removal
      const fallbackZip = new JSZip();
      for (const f of fallbackFiles) {
        fallbackZip.file(f.path, f.content);
      }
      const fallbackBlob = await fallbackZip.generateAsync({ type: 'blob' });
      const fallbackFile = new File([fallbackBlob], file.name.replace(/\.zip$/, '_cleaned.zip'), { type: 'application/zip' });
      additionallyTruncatedFiles.push(...fallbackTruncated);
      truncatedFiles.push(...fallbackTruncated);
      console.log('[FileUploader] Bulk irrelevant truncation: removed', fallbackTruncated.length, 'files, new size:', fallbackFile.size);
    }
    // If still too large, fallback to one-by-one largest file removal
    if (cleanedZipFile.size > MAX_ZIP_SIZE_BYTES) {
      let oneByOneFiles = [...fallbackFiles];
      let oneByOneTruncated: string[] = [];
      while (cleanedZipFile.size > MAX_ZIP_SIZE_BYTES && oneByOneFiles.length > 0) {
        oneByOneFiles.sort((a, b) => b.size - a.size);
        const removed = oneByOneFiles.shift();
        if (removed) {
          oneByOneTruncated.push(removed.path);
          // Re-zip
          const oneByOneZip = new JSZip();
          for (const f of oneByOneFiles) {
            oneByOneZip.file(f.path, f.content);
          }
          const oneByOneBlob = await oneByOneZip.generateAsync({ type: 'blob' });
          const oneByOneFile = new File([oneByOneBlob], file.name.replace(/\.zip$/, '_cleaned.zip'), { type: 'application/zip' });
          console.log('[FileUploader] Fallback one-by-one truncation: removed', removed.path, 'new size:', oneByOneFile.size);
        }
      }
      additionallyTruncatedFiles.push(...oneByOneTruncated);
      truncatedFiles.push(...oneByOneTruncated);
      if (cleanedZipFile.size > MAX_ZIP_SIZE_BYTES) {
        alert(`Even after removing all files, your ZIP is still too large to upload. Please try a smaller project.`);
        throw new Error('ZIP too large after all truncation attempts');
      }
    }
  }

  return { cleanedZip: cleanedZipFile, truncatedFiles, cleanedSize: cleanedZipFile.size, originalSize: file.size, additionallyTruncatedFiles };
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  accept = '.zip',
  disabled = false,
  loading = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        alert('Only .zip files are allowed.');
        return;
      }
      // Clean and truncate ZIP before upload
      try {
        const { cleanedZip, truncatedFiles, cleanedSize, originalSize, additionallyTruncatedFiles } = await cleanAndTruncateZip(file);
        console.log('[FileUploader] Cleaned ZIP created, validating final size...');
        console.log('[FileUploader] Reported cleanedSize:', cleanedSize, 'bytes');
        console.log('[FileUploader] Actual cleanedZip.size:', cleanedZip.size, 'bytes');
        
        // Final validation: check the actual file size
        if (cleanedZip.size > MAX_ZIP_SIZE_BYTES) {
          console.error('[FileUploader] Final ZIP still too large:', cleanedZip.size, 'vs limit', MAX_ZIP_SIZE_BYTES);
          alert(`Even after aggressive truncation, the ZIP file is still too large (${(cleanedZip.size / 1000000).toFixed(1)}MB). Please remove more files manually and try again.`);
          return;
        }
        
        console.log('[FileUploader] Final ZIP size validation passed:', cleanedZip.size, 'bytes');
        console.log('[FileUploader] onFileSelect: cleanedSize', cleanedSize, 'originalSize', originalSize);
        
        // Pass all truncation info to parent
        const allTruncatedFiles = [...truncatedFiles, ...additionallyTruncatedFiles];
        onFileSelect(cleanedZip, allTruncatedFiles, cleanedSize, originalSize, additionallyTruncatedFiles);
      } catch (error) {
        console.error('[FileUploader] Error processing ZIP:', error);
        alert('Failed to process ZIP file: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click?.();
  };

  return (
    <div className="file-uploader" data-testid="file-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        data-testid="file-input"
      />
      <Button
        type="primary"
        size="large"
        icon={<FileTextOutlined />}
        onClick={handleClick}
        disabled={disabled}
        loading={loading}
        className="upload-button"
        data-testid="upload-button"
      >
        Choose ZIP File
      </Button>
    </div>
  );
}; 